import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { getAllActiveTenantConfigs } from "../actions/Tenants/actions";
import { ConfigItemType } from "../actions/Tenants/config/constantsTyped";
import { callAvosApi } from "../utils/useAvosApiHook";

export const useTenantActiveConfigs = (tenantId: string | undefined) => useQuery({
    queryKey: ["tenantActiveConfigs", tenantId],
    queryFn: async (): Promise<ConfigItemType[]> => {
        const { data } = await callAvosApi(`/tenants/${tenantId}/configs/active`);
        return data;
    },
    enabled: !!tenantId,
});

export type TenantConfigRevision = {
    type: string;
    value: string;
    note?: string;
    position?: number;
    config: Record<string, unknown>;
};

/** Save a tenant config as a new revision and make it the active one (its siblings with the same type and value go inactive). */
export const useSaveTenantConfig = (tenantId: string) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation({
        mutationFn: async (revision: TenantConfigRevision) => {
            const { data } = await callAvosApi(`/tenants/${tenantId}/configs`, {
                method: "POST",
                data: { position: 0, ...revision, config: { ...revision.config, value: revision.value } },
            });
            await callAvosApi(`/tenants/${tenantId}/configs/${data.id}`, { method: "PATCH", data: { field: "active", value: true } });
            return data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["tenantActiveConfigs", tenantId] });
            // * Until the config provider reads TanStack Query, it still reads the configs from the Redux store.
            dispatch(getAllActiveTenantConfigs(tenantId) as any);
        },
    });
};
